import { useState, useCallback } from 'react';

import { useTranslation } from 'react-i18next';

import { Filter, SearchRequest } from 'core/util/Search';
import { getImportJob, getImportJobRows, getNotification } from 'directory/listings/notification-modals/api';
import {
    FIELD_TO_PROFILE_FIELD,
    FIELD_TO_VALUES_KEY,
    FIELD_CONFIG,
    BUSINESS_HOUR_PREFIX,
    LOCATION_UID_KEY,
    LOCATION_NAME_KEY,
    MAX_PROFILE_COUNT,
    WEEKDAYS,
    BUSINESS_HOURS_CLOSED,
    TRANSLATION_NAMESPACE,
    ROW_STATUS,
} from 'directory/listings/notification-modals/constants';
import {
    LocationDataRow,
    FieldType,
    ImportJob,
    ImportJobRow,
    ImportType,
    UseCompletionDataResult,
    UseCompletionDataParams,
} from 'directory/listings/notification-modals/types';
import { extractFieldFromNotification } from 'directory/listings/notification-modals/utils';
import { searchProfilesElastic } from 'directory/profiles/Profiles';

const extractLocationName = (row: any, t: (key: string) => string): string => {
    return row.headerValues?.[LOCATION_NAME_KEY] || t('UNKNOWN_LOCATION');
};

const extractLocationUid = (row: any): number | null => {
    const uid = row.values?.[LOCATION_UID_KEY];

    if (!uid) return null;
    return typeof uid === 'number' ? uid : parseInt(uid, 10);
};

const extractValidationError = (row: any, t: (key: string) => string): string | null => {
    if (!row.validationErrors || !Array.isArray(row.validationErrors) || row.validationErrors.length === 0) {
        return null;
    }
    return row.validationErrors[0]?.message || t('VALIDATION_ERROR_OCCURRED');
};

const parseBusinessHoursFromValues = (values: any, t: (key: string) => string): any[] | null => {
    if (!values) return null;

    const hours = WEEKDAYS.map((day) => {
        const hourValue = values[BUSINESS_HOUR_PREFIX + day];

        if (hourValue === undefined || hourValue === null) {
            return null;
        }

        if (hourValue === BUSINESS_HOURS_CLOSED) {
            return {
                day: t(day.toUpperCase()),
                hours: t('CLOSED'),
            };
        }

        const formattedHours = hourValue.replace(/-/g, ' - ');

        return {
            day: t(day.toUpperCase()),
            hours: formattedHours,
        };
    }).filter(Boolean);

    return hours.length > 0 ? hours : null;
};

const extractGoogleData = (values: any, field: FieldType, t: (key: string) => string): string | any[] | null => {
    if (!values) return null;

    switch (field) {
        case 'PHONE_NUMBER':
            return values[FIELD_TO_VALUES_KEY.PHONE_NUMBER] || null;
        case 'WEBSITE_LINK':
            return values[FIELD_TO_VALUES_KEY.WEBSITE_LINK] || null;
        case 'BUSINESS_HOURS':
            return parseBusinessHoursFromValues(values, t);
        case 'DESCRIPTION':
            return values[FIELD_TO_VALUES_KEY.DESCRIPTION] || null;
        default:
            return null;
    }
};

export const useCompletionData = ({
    agencyId,
    tenantId,
    notificationId,
    importJobId,
    apiField,
}: UseCompletionDataParams): UseCompletionDataResult => {
    const { t } = useTranslation(TRANSLATION_NAMESPACE);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<Error | null>(null);
    const [completionData, setCompletionData] = useState<LocationDataRow[]>([]);
    const [stats, setStats] = useState({
        totalProfiles: 0,
        profilesUpdated: 0,
        profilesWithErrors: 0,
    });
    const [field, setField] = useState<FieldType>(apiField || 'UNKNOWN');
    const [totalDurationMillis, setTotalDurationMillis] = useState<number>(0);

    const fetchCompletionData = useCallback(async () => {
        if (!agencyId || !tenantId || !importJobId) {
            setError(new Error(t('REQUIRED_PARAMS_ERROR')));
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const parallelPromises: Promise<any>[] = [];

            parallelPromises.push(
                getImportJob(tenantId, importJobId, {
                    includeUsers: true,
                    includeMapping: false,
                }),
            );

            if ((!apiField || apiField === 'UNKNOWN') && notificationId) {
                parallelPromises.push(getNotification(tenantId, notificationId));
            } else {
                parallelPromises.push(Promise.resolve(null));
            }

            const importJobRowsSearch = {
                filters: [
                    {
                        name: 'importJobRow.importJobID',
                        values: [importJobId],
                    },
                ],
            };

            parallelPromises.push(
                getImportJobRows(tenantId, importJobRowsSearch, {
                    includeDiffs: false,
                    importType: ImportType.Profile,
                }),
            );

            const [importJob, notification, importJobRows] = await Promise.all(parallelPromises);

            let resolvedField = apiField;

            if ((!resolvedField || resolvedField === 'UNKNOWN') && notification) {
                resolvedField = extractFieldFromNotification(notification.parameters);
            }
            if (resolvedField) {
                setField(resolvedField);
            }

            const metrics = (importJob as ImportJob).commitPhase?.importJobMetric?.diffMetric;

            if (metrics) {
                setStats({
                    totalProfiles: metrics.total || 0,
                    profilesUpdated: (metrics.updates || 0) + (metrics.unchanged || 0),
                    profilesWithErrors: (metrics.errors || 0) + (metrics.failed || 0),
                });
            }

            const totalDuration =
                ((importJob as ImportJob).validationPhase?.durationMillis || 0) +
                ((importJob as ImportJob).commitPhase?.durationMillis || 0);

            setTotalDurationMillis(totalDuration);

            const rows = importJobRows as ImportJobRow[];

            if (rows.length === 0) {
                setCompletionData([]);
                setLoading(false);
                return;
            }

            const locationUids = rows
                .map((row) => extractLocationUid(row))
                .filter((uid): uid is number => uid !== null);

            if (locationUids.length === 0) {
                console.warn('No location UIDs found in import job rows');
                setCompletionData([]);
                setLoading(false);
                return;
            }

            const profileFieldName = resolvedField ? FIELD_TO_PROFILE_FIELD[resolvedField] : '';
            const profileSearchRequest: SearchRequest = {
                count: MAX_PROFILE_COUNT,
                filters: [
                    {
                        name: LOCATION_UID_KEY,
                        values: locationUids.map(String),
                    } as Filter,
                ],
                sorts: [
                    {
                        name: LOCATION_NAME_KEY,
                        sortOrder: 1,
                    },
                ],
                projections: [LOCATION_UID_KEY, LOCATION_NAME_KEY, profileFieldName].filter(Boolean),
            };

            const { profiles } = await searchProfilesElastic(agencyId, tenantId, profileSearchRequest, {
                skipEntityMetadataCall: true,
            });

            const fieldConfig = FIELD_CONFIG[resolvedField || 'UNKNOWN'];

            const transformedData = rows
                .map((row, index) => {
                    const locationUid = extractLocationUid(row);

                    if (!locationUid) {
                        console.warn('Missing location UID for row:', row);
                        return null;
                    }

                    const locationName = extractLocationName(row, t);
                    const validationError = extractValidationError(row, t);

                    const profile = profiles.find((p) => p.locationUid === locationUid);

                    const googleData = extractGoogleData(row.values, resolvedField || 'UNKNOWN', t);

                    let reputationData: string | any[] | null = null;

                    if (profile?.fields) {
                        const profileField = FIELD_TO_PROFILE_FIELD[resolvedField || 'UNKNOWN'];

                        if (profileField) {
                            const fieldData = profile.fields[profileField];

                            switch (resolvedField) {
                                case 'WEBSITE_LINK':
                                    reputationData = fieldData || null;
                                    break;
                                case 'PHONE_NUMBER':
                                    reputationData =
                                        Array.isArray(fieldData) && fieldData.length > 0
                                            ? fieldData[0]?.phone || null
                                            : null;
                                    break;
                                case 'BUSINESS_HOURS':
                                case 'DESCRIPTION':
                                    reputationData = fieldData || null;
                                    break;
                                default:
                                    reputationData = null;
                            }
                        }
                    }

                    const hasValidationError = validationError !== null;

                    if (!hasValidationError) {
                        reputationData = googleData;
                    }

                    return {
                        id: `${locationUid}-${index}`,
                        locationUid,
                        locationName: profile?.internalName || locationName,
                        googleData,
                        reputationData,
                        fieldType: fieldConfig.type,
                        field: resolvedField || 'UNKNOWN',
                        fieldIcon: fieldConfig.icon,
                        status: hasValidationError ? ROW_STATUS.ERROR : ROW_STATUS.SUCCESS,
                        errorMessage: validationError || undefined,
                    } as LocationDataRow;
                })
                .filter((row): row is LocationDataRow => row !== null);

            setCompletionData(transformedData);
        } catch (err) {
            setError(err instanceof Error ? err : new Error(t('UNKNOWN_ERROR')));
            console.error('Error fetching completion data:', err);
        } finally {
            setLoading(false);
        }
    }, [agencyId, tenantId, notificationId, importJobId, apiField, t]);

    return {
        loading,
        error,
        completionData,
        stats,
        field,
        totalDurationMillis,
        fetchCompletionData,
    };
};
